"use client";
import { useMemo } from "react";
import { useOffers } from "../payment-queries";
import {
  DURATION_MONTHLY,
  DURATION_YEARLY,
  PRO_FEATURES,
  PAYG_FEATURES,
} from "@/features/pricing/pricing-data";
import {
  SubscriptionPlanId,
  type PlanTypes,
  type PlanDurations,
} from "../types";
import { buildOfferCharge } from "../lib/offer-pricing";
import { PRO_OFFER_ID } from "@/config/billing-constants";

interface UsePricingPlansResult {
  proMonthly: PlanTypes | null;
  proYearly: PlanTypes | null;
  payAsYouGo: PlanTypes;
  DURATION_MONTHLY: PlanDurations;
  DURATION_YEARLY: PlanDurations;
  isLoading: boolean;
}

export function usePricingPlans(seats = 1): UsePricingPlansResult {
  const { data: offers, isLoading } = useOffers();

  const proOffer = useMemo(
    () => offers?.find((offer) => offer.offerId === PRO_OFFER_ID),
    [offers]
  );

  const proMonthly = useMemo<PlanTypes | null>(() => {
    if (!proOffer) return null;
    return {
      planId: SubscriptionPlanId.Speak_Custom,
      offerId: proOffer.offerId,
      features: {
        ...proOffer.features,
        transcriptionHours: proOffer.features.transcriptionHours * seats,
        chatCharacters: proOffer.features.chatCharacters * seats,
        translationCharacters: proOffer.features.translationCharacters * seats,
        textNotes: proOffer.features.textNotes * seats,
        teamMembers: seats,
      },
      name: "Pro",
      displayName: "Pro",
      description: "Your conversation intelligence platform.",
      includedTitle: "Includes",
      included: PRO_FEATURES.map((f) => ({ key: f })),
      ...buildOfferCharge(proOffer.pricing, false, seats),
    };
  }, [proOffer, seats]);

  const proYearly = useMemo<PlanTypes | null>(() => {
    if (!proOffer) return null;
    return {
      planId: SubscriptionPlanId.Speak_Custom,
      offerId: proOffer.offerId,
      features: {
        ...proOffer.features,
        transcriptionHours: proOffer.features.transcriptionHours * seats,
        chatCharacters: proOffer.features.chatCharacters * seats,
        translationCharacters: proOffer.features.translationCharacters * seats,
        textNotes: proOffer.features.textNotes * seats,
        teamMembers: seats,
      },
      name: "Pro",
      displayName: "Pro",
      description: "Your conversation intelligence platform.",
      includedTitle: "Includes",
      included: PRO_FEATURES.map((f) => ({ key: f })),
      ...buildOfferCharge(proOffer.pricing, true, seats),
    };
  }, [proOffer, seats]);

  const payAsYouGo = useMemo<PlanTypes>(
    () => ({
      planId: SubscriptionPlanId.Speak_Pay_As_You_Go,
      name: "Pay As You Go",
      displayName: "Pay As You Go",
      description: "Transcribe and analyze with no commitment.",
      includedTitle: "Includes",
      included: PAYG_FEATURES.map((f) => ({ key: f })),
      totalCost: 0,
      discountedCost: 0,
      discountedPercentage: 0,
      saving: 0,
    }),
    [],
  );

  return {
    proMonthly,
    proYearly,
    payAsYouGo,
    DURATION_MONTHLY,
    DURATION_YEARLY,
    isLoading,
  };
}
